/**
 * Journey page: Controls & Trust (J6 + J7)
 *
 * Peak state: workflow paused at approval gate, comment bubble on text frame.
 * - J6: ApprovalCard floating on canvas, workflow paused, Approval right-panel tab
 * - J7: AgentSubtitleBar as comment indicator on TextFrame, Comment right-panel tab
 */

import { useState, useRef, useEffect } from "react";
import { useCanvasTransform } from "@/workshop/components/canvas/canvas-viewport";
import { WorkshopLayoutProvider } from "@/workshop/components/layout/layout-provider";
import { WorkshopMasterLayout } from "@/workshop/components/layout/master-layout";
import { ApprovalCard } from "@/workshop/components/canvas/approval-card";
import { AgentSubtitleBar } from "@/workshop/components/canvas/agent-subtitle-bar";
import { SearchFrame } from "@/workshop/components/shapes/search-frame/search-frame";
import { TextFrame } from "@/workshop/components/shapes/text-frame/text-frame";
import { WorkflowFrame } from "@/workshop/components/shapes/workflow-frame/workflow-frame";
import {
  MOCK_CONTROLS_TRUST,
  MOCK_SEARCH_RESULTS,
  MOCK_WORKFLOW_STEPS,
} from "@/workshop/data/mock-journeys";

// ─── Types ────────────────────────────────────────────────────────────────────

type Pos = { x: number; y: number };

// ─── Initial frame positions ──────────────────────────────────────────────────

const INITIAL_POS = {
  search: { x: 300, y: 60 } as Pos,
  text: { x: 560, y: 40 } as Pos,
  workflow: { x: 300, y: 340 } as Pos,
};

// ─── Paused workflow steps ────────────────────────────────────────────────────

const PAUSED_WORKFLOW_STEPS = MOCK_WORKFLOW_STEPS.map((step) => {
  if (step.id === "wf3") return { ...step, status: "completed" as const };
  return step;
});

// ─── Canvas content ───────────────────────────────────────────────────────────

function CanvasContent() {
  // --- positions & drag ---
  const [pos, setPos] = useState(INITIAL_POS);
  const drag = useRef<{
    key: string;
    sx: number;
    sy: number;
    px: number;
    py: number;
  } | null>(null);
  const { zoom, isPanMode } = useCanvasTransform();
  const dotR = `${1 / zoom}px`;
  const dotSpacing = `${24 / zoom}px`;
  const zoomRef = useRef(zoom);
  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  useEffect(() => {
    const move = (e: MouseEvent) => {
      if (!drag.current) return;
      const { key, sx, sy, px, py } = drag.current;
      setPos((p) => ({
        ...p,
        [key]: {
          x: px + (e.clientX - sx) / zoomRef.current,
          y: py + (e.clientY - sy) / zoomRef.current,
        },
      }));
    };
    const up = () => {
      drag.current = null;
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
  }, []);

  function grab(key: string, e: React.MouseEvent) {
    if (isPanMode) return;
    e.preventDefault();
    drag.current = {
      key,
      sx: e.clientX,
      sy: e.clientY,
      px: pos[key as keyof typeof pos].x,
      py: pos[key as keyof typeof pos].y,
    };
  }

  return (
    <div
      className="relative h-full w-full overflow-hidden bg-white dark:bg-black"
      data-testid="controls-trust-canvas"
      style={{
        backgroundImage: `radial-gradient(circle, var(--color-canvas-dot) ${dotR}, transparent ${dotR})`,
        backgroundSize: `${dotSpacing} ${dotSpacing}`,
      }}
    >
      {/* Frame: SearchFrame — done */}
      <div
        className="absolute z-10 cursor-grab active:cursor-grabbing select-none"
        style={{ left: pos.search.x, top: pos.search.y, width: 220 }}
        onMouseDown={(e) => grab("search", e)}
        data-testid="canvas-frame-search"
      >
        <SearchFrame
          query="AI content trends 2025"
          results={MOCK_SEARCH_RESULTS}
          status="done"
          agentName="Aria"
        />
      </div>

      {/* Comment pill anchored above the TextFrame — moves with text frame */}
      <div
        className="absolute z-10"
        style={{ left: pos.text.x, top: pos.text.y - 32 }}
        data-testid="text-frame-comment-pill-wrapper"
      >
        <AgentSubtitleBar
          agentName="You"
          actionText="Make the intro shorter"
          dotColor="bg-warning"
          data-testid="text-frame-comment-pill"
        />
      </div>

      {/* Frame: TextFrame — done, with comment pill above */}
      <div
        className="absolute z-10 cursor-grab active:cursor-grabbing select-none"
        style={{ left: pos.text.x, top: pos.text.y, width: 240 }}
        onMouseDown={(e) => grab("text", e)}
        data-testid="canvas-frame-text"
      >
        <TextFrame
          title="Blog Draft"
          text={
            "## Introduction\nAI-powered content teams are reshaping the marketing landscape in 2025. Here's what's working — and what's not.\n\n## Key Trends\n1. Autonomous multi-agent content pipelines\n2. Real-time personalisation at scale\n3. Human-in-the-loop review workflows"
          }
          status="done"
          agentName="Aria"
        />
      </div>

      {/* Frame: WorkflowFrame — paused at approval gate */}
      <div
        className="absolute z-10 cursor-grab active:cursor-grabbing select-none"
        style={{ left: pos.workflow.x, top: pos.workflow.y, width: 340 }}
        onMouseDown={(e) => grab("workflow", e)}
        data-testid="canvas-frame-workflow"
      >
        <WorkflowFrame
          title="Email Campaign Workflow"
          flowSteps={PAUSED_WORKFLOW_STEPS}
          status="streaming"
          agentName="Aria"
        />
      </div>

      {/* ApprovalCard — floating overlay on canvas, not draggable */}
      <div
        className="absolute z-20"
        style={{ left: 570, top: 270 }}
        data-testid="canvas-approval-card-wrapper"
      >
        <ApprovalCard
          action={MOCK_CONTROLS_TRUST.approval.action}
          description={MOCK_CONTROLS_TRUST.approval.description}
          riskLevel={MOCK_CONTROLS_TRUST.approval.riskLevel}
        />
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function ControlsTrustPage() {
  return (
    <WorkshopLayoutProvider defaultSidebarOpen defaultRightPanelOpen>
      <svg style={{ display: "none" }} aria-hidden="true">
        <defs>
          <filter
            id="liquid-glass-distortion"
            x="-10%"
            y="-10%"
            width="120%"
            height="120%"
            colorInterpolationFilters="sRGB"
          >
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.018 0.014"
              numOctaves="3"
              seed="5"
              result="noise"
            />
            <feColorMatrix
              type="saturate"
              values="0"
              in="noise"
              result="grayNoise"
            />
            <feDisplacementMap
              in="SourceGraphic"
              in2="grayNoise"
              scale="2"
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
        </defs>
      </svg>
      <div
        className="h-screen w-screen overflow-hidden"
        data-testid="controls-trust-page"
      >
        <WorkshopMasterLayout>
          <CanvasContent />
        </WorkshopMasterLayout>
      </div>
    </WorkshopLayoutProvider>
  );
}
